/**
 * Hedge decisions for the two whole-parcel FEMA flood controls: the blocking
 * grounding gate and the deterministic narrative reconciler.
 *
 * Both stand down when a whole-parcel zone claim reads as non-assertive ("the
 * entire site is NOT in Zone A"). Flood prose is full of negations that hedge
 * nothing — mandated disclosures ("complete coverage is not claimed"),
 * neighbouring findings ("the review found no wetlands") and ordinary
 * conclusions ("mitigation is not required AS the entire site is in Zone X") —
 * so the decision is a POSITIVE list of hedge forms. Anything unrecognised
 * leaves the control ARMED rather than disarming it.
 *
 * The two controls are deliberately asymmetric, because their failure modes
 * are opposite:
 *
 *   floodAssertionIsHedged — used by the BLOCKING gate. Over-firing merely
 *   blocks a report for repair (fails closed), so it suppresses only on a
 *   tightly attached hedge: a negation in the assertion's own head or in its
 *   placement predicate within two words ("is NOT located WITHIN"), a
 *   quantifier or denial frame anchored immediately before the noun phrase, an
 *   exclusion predicate ("is excluded from"), an adjacent interrogative frame,
 *   or a question mark.
 *
 *   floodClausePolarityIsAmbiguous — used by the control that REWRITES prose.
 *   Substituting inside a clause whose polarity is unclear produces a
 *   reader-facing sentence that denies the true result ("It is inaccurate to
 *   state that <the true zone mix>"), so it stands down on any negation,
 *   denial, interrogative or exception frame anywhere in the clause. Claims it
 *   passes over are still caught by the gate, which production report
 *   generation runs with REPORT_GROUNDING on.
 *
 * Mandated disclosure phrases are blanked in place (length preserving, so
 * every index stays valid) before either decision, and parenthetical asides
 * are dropped: neither negates the claim it sits beside.
 */

/** Mandated disclosure phrases this codebase can emit inside a clause. */
const FLOOD_DISCLOSURE_PHRASES = new RegExp([
  'complete coverage is not claimed(?: at this precision| on this fallback path)?',
  '(?:the )?(?:flood |site-wide )?(?:intersection|geometry) (?:is|was) not exact',
  'it is not exact',
  '(?:the fit )?(?:is )?not (?:reported as )?fully applied',
  'remaining (?:land|acreage) (?:is |remains )?not confirmed flood-free',
  'no complete screening claim is available',
  'completeness is not established',
  '(?:coverage )?geometry could not be (?:verified|clipped)',
  'could not be completed for \\d+ positive feature\\(s\\)',
  'are not in that bound',
  'remains unmapped/unknown, not Zone X',
  'did not supply more complete usable coverage',
].join('|'), 'giu');


/** Same removal, length preserving, so indices into the text stay valid. */
export function blankFloodDisclosurePhrases(text: string): string {
  return text.replace(FLOOD_DISCLOSURE_PHRASES, match => ' '.repeat(match.length));
}

/** Independent-statement boundaries. */
const CLAUSE_BOUNDARY = /[;—–]|(?: - )/g;
/** Parenthetical asides do not negate the claim they sit beside. */
const PARENTHETICAL = /\([^)]*\)/g;
/**
 * Negations that can negate a PLACEMENT, and so may hedge the claim when they
 * sit in its head. Words like "excluded", "exempt" or "inaccurate" are not
 * here: they negate something else ("the floodway is excluded, the entire site
 * is in Zone X") and reach the claim only through the anchored exclusion
 * predicate or denial frame.
 */
const PLACEMENT_NEGATION = /\b(?:not|never|neither|nor|no|none|nowhere|nothing|hardly|barely|scarcely|rarely)\b|\w+n['\u2019]t\b/iu;
/** Negation tokens, including contractions and downtoners. */
const NEGATION = /\b(?:not|never|neither|nor|no|none|nowhere|nothing|without|excluding|excluded|exempt|other than|hardly|barely|scarcely|rarely|false|incorrect|inaccurate)\b|\w+n['\u2019]t\b/iu;
/**
 * Words allowed between a negation and the assertion head. Anything else (a
 * connective, a content verb, a noun, punctuation other than a comma) means
 * the negation belongs to a different predicate and does not hedge.
 */
const HEAD_TOKEN = /^(?:the|a|an|this|that|these|those|its|their|our|his|her|site'?s?|parcel'?s?|property'?s?|is|are|was|were|be|been|being|does|do|did|has|have|had|will|would|shall|should|can|could|may|might|must|lie|lies|lay|located|situated|within|in|of|part|any|all|entirely|wholly|fully|actually|fact|indeed|currently|presently|however|itself|therefore|thus|not|never|no|neither|nor|none|nowhere|nothing|hardly|barely|scarcely|rarely|\w+n['\u2019]t)$/iu;
/** Words that place the parcel in a zone: a negation before one of these
 * negates the placement itself. */
const LOCATIVE = /^(?:within|in|inside|into|located|situated|lie|lies|lying|falls?|sits?|zone|sfha|floodplain)$/iu;
/** A negation that lands on the placement itself, within two words. */
const NEGATED_PLACEMENT = new RegExp(
  // "nor" continues a denial ("is not within Zone A, NOR WITHIN Zone AE").
  // There is deliberately NO subjunctive here: reading were/would/could as a
  // hedge suppressed ordinary modal claims ("the entire site WOULD BE LOCATED
  // WITHIN Zone X"), which is the escape round 23 introduced and round 24
  // removed. A hypothetical now blocks, like every other unattached form.
  String.raw`\b(?:not|never|no|none|neither|nor|nothing|hardly|barely|scarcely|rarely|\w+n['’]t)\b[\s,]*`
  // only auxiliaries and degree adverbs may stand between the negation and the
  // placement; a noun ("no WETLANDS, is located within") means the negation
  // belongs to a different predicate and must not hedge the zone claim
  + String.raw`(?:(?:be|been|being|is|are|was|were|lie|lies|lying|sit|sits|fall|falls|remain|remains|extend|extends|entirely|wholly|fully|currently|presently|actually|now|yet|part)[\s,]+){0,2}`
  // Whether one of these places the parcel in the CLAIMED zone is decided by
  // negationReachesZone below, not by the shape of the word: "no MAPPED
  // WETLANDS and is located within <zone>" never reaches the zone token.
  // Bare "in" belongs here as much as "within": "the entire site is not IN
  // FEMA flood zone X" is the plainest true denial in this prose, and without
  // it the gate blocked one (fail-closed, but needlessly).
  + String.raw`(?:within|in|inside|into|located|situated|mapped|of\s+(?:the\s+)?(?:sfha|floodplain|flood\s+zone))\b`,
  'iu',
);
/**
 * An exclusion denies the claim only when its complement IS the claimed zone,
 * i.e. the match runs up to the zone token. "Excluded from the mapped
 * floodplain AND OCCUPIES zone X" excludes the site from something else and
 * then asserts the zone; deciding that by listing placement verbs loses to the
 * next synonym, so the position of the zone token decides it instead.
 */
function exclusionReachesZone(predicate: string, zoneOffsetInPredicate: number): boolean {
  // A zone token at or before the assertion is not inside this predicate, so
  // there is nothing here for an exclusion to govern ("FEMA flood zone X
  // covers the entire site, excluded from the floodplain ordinance" excludes
  // the parcel from something else and leaves the zone claim standing).
  // Nor can it govern a zone token in a LATER clause ("excluded from the
  // mapped floodplain; IT OCCUPIES zone X"): the offset then runs past this
  // predicate, where an empty gap would otherwise read as adjacency.
  if (!(zoneOffsetInPredicate > 0) || zoneOffsetInPredicate > predicate.length) return false;
  const match = EXCLUSION_PREDICATE.exec(predicate);
  if (!match) return false;
  // The zone token must be inside the exclusion's own complement ("excluded
  // FROM FEMA FLOOD ZONE A"). Anything else — a comma apposition, a blanked
  // parenthetical, a bare space — is a separate assertion of the zone, not the
  // thing the parcel was excluded from.
  return match.index + match[0].length >= zoneOffsetInPredicate;
}
/**
 * Only zone-describing filler may stand between a negated placement and the
 * claimed zone token. "not located WITHIN <zone>" and "not within THE MAPPED
 * <zone>" deny the claim; "not located within THE REGULATORY FLOODWAY AND IS
 * LOCATED WITHIN <zone>" denies something else and then asserts the claim.
 * Deciding that by listing the verbs that could follow loses to the next
 * synonym, so the distance to the zone token decides it instead.
 */
const ZONE_FILLER = /^(?:\s*(?:the|a|an|any|this|its|mapped|effective|current|currently|designated|adopted|FEMA(?:'s)?|special|flood|hazard|area|areas|sfha|zone|zones|within|in|inside|into|of)\b)*\s*$/iu;
const NEGATED_PLACEMENT_G = new RegExp(NEGATED_PLACEMENT.source, 'giu');
/**
 * A FEMA zone token in the gap is itself a separate assertion, so a negation
 * before it cannot be denying the zone AFTER it. ZONE_FILLER cannot express
 * this: "a" is both an article and a zone letter, so the filler walk stepped
 * straight over "... flood zone A ..." and landed on a later zone.
 */
const ZONE_TOKEN_IN_GAP =
  /\b(?:FEMA(?:'s)?\s+)?(?:flood\s+)?zone\s*[:=\-]?\s*["'“”‘’]?\s*(?:A(?:E|H|O|R|99)?|V(?:E)?|X(?:500)?|B|C|D)\b/i;
function negationReachesZone(predicate: string, zoneOffsetInPredicate: number): boolean {
  if (!(zoneOffsetInPredicate > 0) || zoneOffsetInPredicate > predicate.length) return false;
  NEGATED_PLACEMENT_G.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = NEGATED_PLACEMENT_G.exec(predicate)) !== null) {
    const gap = predicate.slice(match.index + match[0].length);
    if (!ZONE_TOKEN_IN_GAP.test(gap) && ZONE_FILLER.test(gap)) return true;
    if (match[0].length === 0) NEGATED_PLACEMENT_G.lastIndex += 1;
  }
  return false;
}
/** Predicate forms that exclude the parcel from the zone outright. */
const EXCLUSION_PREDICATE = /^[\s,]*(?:is|are|was|were|remains?|sits?|lies?|falls?)?\s*(?:entirely\s+|wholly\s+|fully\s+)?(?:excluded|exempt)\s+from\s+(?:the\s+)?(?:mapped\s+)?(?:FEMA\s+)?(?:effective\s+)?(?:flood\s+)?(?:zone|sfha|floodplain|special flood hazard area|1%|0\.2%)/iu;
/**
 * Quantifier negations, anchored so they must govern the assertion's own noun
 * phrase: only determiners and adjectives of that phrase may follow them. A
 * quantifier about something else ("found no wetlands and the entire site...")
 * is a different predicate and does not hedge.
 */
const QUANTIFIER_NEGATION = /\b(?:no part of|no portion of|none of|neither of|not one of|nothing (?:in|within|on)|nowhere (?:in|within|on|does))\s+(?:the\s+|this\s+|that\s+|its\s+)?(?:entire\s+|whole\s+|full\s+)?$/iu;
/** Frames that negate the proposition immediately following them. */
const HEDGE_FRAME = /\b(?:it is (?:not the case|untrue|incorrect|inaccurate|false|misleading)(?:\s+to\s+(?:state|say|claim|assert|suggest|conclude))?\s+that|contrary to (?:the )?(?:claim|statement|assertion)s? that)\s*$/iu;
/**
 * Interrogative and verification FRAMES that govern what follows them. Past
 * participles are deliberately excluded: "not confirmed flood-free" and "was
 * not established" are descriptive statements, not questions about the claim.
 */
const INTERROGATIVE = /\b(?:if|whether|assuming|suppose)\b/iu;
/**
 * Status words hedge only when they take an interrogative complementiser.
 * "Elevation certificates are PENDING, and the entire site is in Zone X" is
 * the predicate of a different subject, not a question about the claim.
 */
const STATUS_QUESTION = /\b(?:unclear|unresolved|undetermined|tbd|pending)\s+(?:whether|if)\b[\s,]*(?:the\s+|this\s+|that\s+)?(?:entire\s+|whole\s+|full\s+)?$/iu;
/**
 * Verification verbs hedge ONLY with an interrogative complementiser.
 * "confirm WHETHER the entire site is Zone A" is a question; "elevation
 * certificates confirm THAT the entire site is Zone X" is a stronger
 * assertion than the bare claim and must leave the control armed.
 */
const VERIFICATION_QUESTION = /\b(?:confirm|confirms|verify|verifies|determine|determines|assess|assesses|clarify|clarifies|establish|establishes|check|checks)\s+(?:whether|if)\b[\s,]*(?:the\s+|this\s+|that\s+)?(?:entire\s+|whole\s+|full\s+)?$/iu;

/**
 * ONE sentence-end rule for both flood controls. The gate and the rewriter had
 * separate abbreviation lists, they drifted, and the rewriter then ended the
 * claim's sentence before a denial it could not see and substituted the
 * measured mix inside a sentence that denies it.
 *
 * A period ends a sentence unless it closes a dotted initialism ("U.S.",
 * "e.g."), or it closes an ordinary word that is also an abbreviation AND what
 * follows cannot be a new sentence — a lowercase word or a digit. A capital
 * always terminates, so a following question can never be merged into a claim.
 */
/**
 * Never ends a sentence in any context. "U.S." and "a.m." are here too: a
 * capitalised follower is ambiguous between a new sentence ("per the U.S. Is
 * an elevation certificate required?") and a continuing proper noun ("per
 * U.S. Army Corps mapping"), and terminating breaks the second — which drops
 * the zone token out of the claim entirely. The merged-question case is held
 * by the runtime floor instead, which reads the PRE-CHANGE sentence and so
 * never sees the merge.
 */
const ALWAYS_NON_TERMINAL = /\b(?:e\.g|i\.e|u\.s|u\.k|a\.m|p\.m)\.$/i;
const CONTEXTUAL_NON_TERMINAL =
  /\b(?:no|nos|rev|revs|fig|figs|approx|est|vs|etc|al|inc|corp|ltd|ave|rd|mt|dr|mr|mrs|ms|jr|sr|ref|refs|sec|secs|pp|ca|cf|panel|panels|pt|ste|dept|div|elev|hwy|cnty|twp|rng)\.$/i;
/**
 * The REWRITER's bounds, biased LONG: no abbreviation ever ends its clause.
 * The gate wants the short sentence (a merged neighbour could disarm it); the
 * rewriter wants the long one (a denial it cannot see makes it substitute the
 * measured mix inside a sentence that denies it). One shared splitter cannot
 * serve both, so they share the vocabulary and differ only in bias.
 */
export function isRewriterSentenceEnd(text: string, at: number): boolean {
  const character = text[at];
  if (character === '\n') return true;
  if (character !== '.' && character !== '!' && character !== '?') return false;
  const after = text.slice(at + 1);
  if (after.length > 0 && !/^\s/.test(after)) return false;
  if (character === '.') {
    const lead = text.slice(Math.max(0, at - 12), at + 1);
    if (ALWAYS_NON_TERMINAL.test(lead) || CONTEXTUAL_NON_TERMINAL.test(lead)) return false;
  }
  return true;
}

export function isFloodSentenceEnd(text: string, at: number): boolean {
  const character = text[at];
  if (character === '\n') return true;
  if (character !== '.' && character !== '!' && character !== '?') return false;
  const after = text.slice(at + 1);
  if (after.length > 0 && !/^\s/.test(after)) return false;
  if (character === '.') {
    const lead = text.slice(Math.max(0, at - 12), at + 1);
    if (ALWAYS_NON_TERMINAL.test(lead)) return false;
    if (/^\s+[a-z0-9]/.test(after) && CONTEXTUAL_NON_TERMINAL.test(lead)) return false;
  }
  return true;
}

/**
 * The clause carrying the assertion, with disclosure phrases blanked in place
 * (length preserving, so every index stays valid) and parentheticals dropped.
 */
function assertionClause(sentence: string, assertionStart: number, assertionEnd: number): {
  clause: string; clauseStart: number; localStart: number; localEnd: number;
} {
  let clauseStart = 0;
  let clauseEnd = sentence.length;
  CLAUSE_BOUNDARY.lastIndex = 0;
  let boundary: RegExpExecArray | null;
  while ((boundary = CLAUSE_BOUNDARY.exec(sentence)) !== null) {
    const at = boundary.index;
    if (at + boundary[0].length <= assertionStart) clauseStart = at + boundary[0].length;
    else if (at >= assertionEnd) { clauseEnd = at; break; }
  }
  const clause = blankFloodDisclosurePhrases(sentence.slice(clauseStart, clauseEnd))
    .replace(PARENTHETICAL, match => ' '.repeat(match.length));
  return {
    clause,
    clauseStart,
    localStart: Math.max(0, Math.min(assertionStart - clauseStart, clause.length)),
    localEnd: Math.max(0, Math.min(assertionEnd - clauseStart, clause.length)),
  };
}

/**
 * The pre-change predicate, reproduced with its original window: a 24-character
 * lookback from the earlier of the assertion and the zone token, through to the
 * later of the assertion end and the zone token, tested for
 * `if|whether|assuming|not|never|neither|outside|except`, plus a trailing
 * question mark. It suppresses far too often to be a rule on its own, but as a
 * CONJUNCT it makes "never weaker than the control this replaced" true at
 * RUNTIME instead of true only across whatever rows a test corpus happens to
 * hold. Seven review rounds each found a live escape while that corpus was
 * green; this is the property those rounds were trying to establish.
 */
export function preChangeWouldSuppress(sentence: string, assertionStart: number, assertionEnd: number, zoneStart: number): boolean {
  const left = Math.max(0, Math.min(assertionStart, zoneStart) - 24);
  const right = Math.max(assertionEnd, zoneStart);
  return sentence.trim().endsWith('?')
    || /\b(?:if|whether|assuming|not|never|neither|outside|except)\b/i.test(sentence.slice(left, right));
}

/**
 * ADJACENCY ONLY, UNDER A HARD FLOOR. Does a hedge deny THIS zone claim?
 *
 * Seven adversarial review rounds attacked the previous shape of this
 * function, and every one of them found the same class of defect: a denial
 * that governed something else — another feature, another zone, another
 * clause — reaching the claim through a lead frame, and each round's fix was
 * another vocabulary list (of frames, of joins, of continuation words) whose
 * unlisted members reopened it. A list that decides which prose SUPPRESSES a
 * control fails in the dangerous direction by construction.
 *
 * So the lead frames are gone. A hedge counts here only when it is ATTACHED
 * to the placement that reaches the claimed zone token: a negation landing on
 * the placement within two words, or an exclusion whose own complement is the
 * zone token. Both are decided by position, not by which words surround them.
 *
 * This is strictly stronger than the control it replaces — a lead-anchored
 * denial ("No part of the entire site is located within Zone A", "It is not
 * the case that...", "Confirm whether...") is now BLOCKED rather than
 * suppressed. That direction is safe: over-firing sends a true report to the
 * one-shot repair, while under-firing ships a false whole-parcel flood claim
 * to the reader. The rewriter, whose failure mode is the opposite, keeps its
 * own broad test below.
 */
export function floodAssertionIsHedged(
  sentence: string,
  assertionStart: number,
  assertionEnd: number,
  _lookback = 80,
  /**
   * The ZONE TOKEN's start index, which is where the assertion's own predicate
   * is taken to end. The name predates the gate passing the token; it defaults
   * to the clause end when omitted.
   */
  predicateEndIndex?: number,
): boolean {
  const { clause, localEnd, clauseStart } = assertionClause(sentence, assertionStart, assertionEnd);
  // The question must be the CLAIM's own clause. A question in a later
  // independent clause ("... within FEMA flood zone X; is a LOMR required?")
  // asks about something else and leaves the completed claim standing.
  if (clause.trim().endsWith('?')) return true;
  const zoneStart = predicateEndIndex === undefined ? assertionEnd : predicateEndIndex;
  const predicateEnd = predicateEndIndex === undefined
    ? clause.length
    : Math.max(localEnd, Math.min(predicateEndIndex - clauseStart, clause.length));
  const zoneOffset = zoneStart - clauseStart - localEnd;
  // "the entire site is EXCLUDED FROM FEMA FLOOD ZONE A": the zone token is
  // the exclusion's own complement.
  if (exclusionReachesZone(clause.slice(localEnd), zoneOffset)) return true;
  // "is NOT LOCATED WITHIN <zone>": the negation lands on the placement, and
  // only zone-describing filler may stand between it and the zone token. The
  // scan stops at the caller's predicate end and at a comma-introduced
  // relative clause, both of which carry their own subject.
  const predicate = clause.slice(localEnd, predicateEnd).split(/,\s*(?:which|that|who|whose|where)\b/iu)[0] ?? '';
  return negationReachesZone(predicate, zoneOffset);
}

/**
 * The BLOCKING GATE's rule: an attached hedge AND the frozen floor. Neither
 * conjunct is sufficient — the floor alone suppresses on any "not" anywhere
 * near the claim, and the analysis alone is only as good as its own reading of
 * attachment.
 *
 * The floor is evaluated on the PRE-CHANGE sentence, which the caller supplies
 * separately: this gate's own splitter is abbreviation-tolerant, and a floor
 * read on a wider sentence is not the control it claims to reproduce. That was
 * the defect that made "never weaker by construction" false when it was first
 * written.
 */
export function floodClaimIsHedged(
  sentence: string,
  assertionStart: number,
  assertionEnd: number,
  zoneStart: number,
  floorSuppresses: boolean,
): boolean {
  return floorSuppresses && floodAssertionIsHedged(sentence, assertionStart, assertionEnd, 80, zoneStart);
}

/**
 * The pre-change REWRITER's predicate, transcribed from HEAD 47481c8d7's
 * isNonAssertiveContext. The gate got a runtime floor against the control it
 * replaced; this is the same thing for the control that rewrites prose, and it
 * matters more, because the rewriter runs BEFORE any detector snapshots the
 * body: a substitution here erases the claim, so the gate never sees the input
 * it would have blocked. A rewrite of this predicate that quietly drops a
 * token — "outside", bare "except", "determine" — therefore both corrupts
 * reader-facing text AND removes a block. With this conjunct the rewriter can
 * only ever become MORE conservative than the one it replaced.
 */
export function preChangeRewriterWouldStandDown(sentence: string, lead: string): boolean {
  return sentence.trim().endsWith('?')
    || /\b(?:if|whether|assuming|suppose|confirm|determine|verify)\b/iu.test(lead.slice(-80))
    || /\b(?:false|incorrect|inaccurate|not|never|neither|outside|except)\b/iu.test(lead.slice(-80));
}

/**
 * The REWRITER's test, and deliberately the opposite shape: broad, clause-wide
 * and conservative. Rewriting inside a sentence that denies its own claim
 * produces reader-facing text that denies the measured mix, so anything that
 * could be a denial must stop the rewrite — and over-firing here costs only a
 * correction that is appended instead of substituted.
 */
export function floodClauseDeniesClaim(
  sentence: string,
  assertionStart: number,
  assertionEnd: number,
): boolean {
  // The caller passes HEAD's bounds, so a question here really does end the
  // claim's own sentence and a merged abbreviation cannot pull one in from the
  // next one.
  if (sentence.trim().endsWith('?')) return true;
  const { clause, localStart, localEnd } = assertionClause(sentence, assertionStart, assertionEnd);
  const lead = clause.slice(0, localStart);
  // The lead frames stay ANCHORED here, exactly as they were before the gate
  // dropped them. A bare negation or "whether" anywhere in the lead belongs to
  // another subject ("mitigation is not required AS the entire site is in Zone
  // X", "a future study will confirm WHETHER wetlands are present, AND the
  // entire site is in Zone X") and must NOT stop the correction — those are
  // the false claims this rewriter exists to fix.
  if (NEGATION.test(clause.slice(localStart, localEnd))) return true;
  if (QUANTIFIER_NEGATION.test(lead) || HEDGE_FRAME.test(lead)) return true;
  if (VERIFICATION_QUESTION.test(lead) || STATUS_QUESTION.test(lead)) return true;
  const tokens = lead.split(/(\s+|(?=,)|(?<=,))/u).filter(token => token.trim().length > 0);
  let headRunStart = tokens.length;
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i]!.replace(/^[("'`]+|[)"'`.]+$/gu, '');
    if (!token) continue;
    if (PLACEMENT_NEGATION.test(token)) return true;
    if (!HEAD_TOKEN.test(token)) break;
    headRunStart = i;
  }
  if (INTERROGATIVE.test(tokens.slice(-3).join(' '))) return true;
  if (headRunStart < tokens.length && INTERROGATIVE.test(tokens.slice(headRunStart).join(' '))) return true;
  return negationReachesZone(clause.slice(localEnd), clause.length - localEnd)
    || exclusionReachesZone(clause.slice(localEnd), clause.length - localEnd);
}

export function floodClausePolarityIsAmbiguous(
  sentence: string,
  assertionStart: number,
  assertionEnd: number,
): boolean {
  if (floodAssertionIsHedged(sentence, assertionStart, assertionEnd)) return true;
  // The WHOLE sentence, not the assertion's clause: a semicolon or a dash cuts
  // the denial out of the clause ("<claim> — this is not the case"), and the
  // substitution then lands inside a sentence that refutes it. The mandated
  // disclosure wording is blanked first, exactly as the clause path blanks it,
  // so "complete coverage is NOT claimed" beside a false claim still corrects.
  const blanked = blankFloodDisclosurePhrases(sentence);
  return NEGATION.test(blanked) || INTERROGATIVE.test(blanked)
    || /\b(?:inaccurate|incorrect|untrue|false|misleading|disregard|ignore|erroneous|wrong|mistaken|superseded|withdrawn|rebutted|obsolete|disputed|refuted|overtaken)\b/iu.test(blanked)
    || /\b(?:aside from|apart from|save for|except for|besides|with the exception of)\b/iu.test(blanked);
}
